import React from 'react';
import { motion } from 'framer-motion';
import {
  FiX,
  FiUser,
  FiFileText,
  FiCalendar,
  FiClock,
  FiCheckCircle,
  FiXCircle,
  FiFlag,
  FiCheckSquare,
  FiHash,
  FiLayers,
  FiType,
  FiBriefcase,
  FiDollarSign,
  FiPhone,
  FiMapPin,
  FiInfo
} from 'react-icons/fi';

const DemandeDetail = ({ demande, onClose }) => {
  if (!demande || !demande.ticket) return null;

  const {
    id,
    chef_de_projet_id,
    bon_commande_id,
    nbr_jrs,
    nbr_jrs_valider,
    confirmation_admin,
    created_at,
    updated_at,
    ticket
  } = demande;

  const statusKey =
    confirmation_admin === '1'
      ? 'acceptee'
      : confirmation_admin === '2'
      ? 'refusee'
      : 'en_attente';

  const statusData = {
    acceptee: {
      icon: <FiCheckCircle className="text-green-500" />,
      text: 'Acceptée',
      color: 'bg-green-100 text-green-800',
      border: 'border-l-green-500'
    },
    refusee: {
      icon: <FiXCircle className="text-red-500" />,
      text: 'Refusée',
      color: 'bg-red-100 text-red-800',
      border: 'border-l-red-500'
    },
    en_attente: {
      icon: <FiClock className="text-yellow-500" />,
      text: 'En attente',
      color: 'bg-yellow-100 text-yellow-800',
      border: 'border-l-yellow-500'
    }
  };

  const status = statusData[statusKey];

  // Fonction pour formater la date
  const formatDate = (dateString) => {
    if (!dateString) return 'Non spécifié';
    const options = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString('fr-FR', options);
  };

  return (
    <motion.div
      className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="bg-white rounded-lg shadow-xl w-full max-w-5xl relative max-h-[90vh] overflow-hidden flex flex-col"
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
      >
        {/* Header */}
        <div className="bg-[#875A7B] text-white p-4 flex justify-between items-center sticky top-0 z-10">
          <div className="flex items-center space-x-3">
            <FiFileText size={20} />
            <h2 className="text-xl font-bold">Demande #{id}</h2>
          </div>
          <button 
            onClick={onClose} 
            className="p-1 rounded-full hover:bg-white hover:bg-opacity-20 transition-colors"
            aria-label="Fermer"
          >
            <FiX size={24} />
          </button>
        </div>

        {/* Main Content */}
        <div className="overflow-y-auto flex-1">
          {/* Status and Basic Info */}
          <div className={`border-l-4 ${status.border} p-5 bg-white shadow-sm`}>
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center space-x-3">
                <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${status.color}`}>
                  {status.icon}
                  <span className="ml-2">{status.text}</span>
                </div>
                <div className="text-sm text-gray-500">
                  <span className="font-medium">Créé le:</span> {formatDate(created_at)}
                </div>
                <div className="text-sm text-gray-500">
                  <span className="font-medium">Modifié le:</span> {formatDate(updated_at)}
                </div>
              </div>
              
              {bon_commande_id && (
                <div className="bg-blue-50 text-blue-800 px-3 py-1 rounded-full text-sm font-medium flex items-center">
                  <FiHash size={14} className="mr-2" />
                  Bon de commande: #{bon_commande_id}
                </div>
              )}
            </div>
          </div>

          {/* Grid Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 p-6">
            {/* Colonne 1: Informations principales */}
            <div className="space-y-6">
              {/* Ticket Info */}
              <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm">
                <h3 className="text-lg font-semibold text-[#875A7B] mb-4 flex items-center">
                  <FiFileText className="mr-2" /> Informations du Ticket
                </h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Nom</label>
                    <p className="mt-1 text-gray-900">{ticket.nom || 'Non spécifié'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Type</label>
                    <p className="mt-1 text-gray-900">{ticket.type || 'Non spécifié'}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Statut</label>
                    <p className="mt-1 text-gray-900">{ticket.status || 'Non spécifié'}</p>
                  </div>
                </div>
              </div>

              {/* Jours de travail */}
              <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm">
                <h3 className="text-lg font-semibold text-[#875A7B] mb-4 flex items-center">
                  <FiCheckSquare className="mr-2" /> Jours de travail
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Demandés</label>
                    <p className="mt-1 text-2xl font-bold text-[#875A7B]">
                      {nbr_jrs ?? '--'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Validés</label>
                    <p className={`mt-1 text-2xl font-bold ${
                      nbr_jrs_valider ? 'text-green-600' : 'text-gray-400'
                    }`}>
                      {nbr_jrs_valider ?? '--'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Colonne 2: Description et dates */}
            <div className="space-y-6">
              {/* Description */}
              <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm h-full">
                <h3 className="text-lg font-semibold text-[#875A7B] mb-4 flex items-center">
                  <FiType className="mr-2" /> Description
                </h3>
                <div className="prose prose-sm max-w-none text-gray-700">
                  {ticket.content ? (
                    <p className="whitespace-pre-line">{ticket.content}</p>
                  ) : (
                    <div className="text-gray-400 italic flex items-center">
                      <FiInfo className="mr-2" /> Aucune description fournie
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Colonne 3: Organisation */}
            <div className="space-y-6">
              <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm">
                <h3 className="text-lg font-semibold text-[#875A7B] mb-4 flex items-center">
                  <FiBriefcase className="mr-2" /> Organisation
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Nom</label>
                    <p className="mt-1 text-gray-900">
                      {ticket.organisation?.nomOrganisation || 'Non spécifié'}
                    </p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-500">Téléphone</label>
                      <p className="mt-1 text-gray-900 flex items-center">
                        <FiPhone className="mr-2 text-gray-400" size={14} />
                        {ticket.organisation?.telephone || 'Non spécifié'}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-500">Prix/Jour</label>
                      <p className="mt-1 text-gray-900 flex items-center">
                        <FiDollarSign className="mr-2 text-gray-400" size={14} />
                        {ticket.organisation?.prix_jour ? `${ticket.organisation.prix_jour} MAD` : 'Non spécifié'}
                      </p>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Adresse</label>
                    <p className="mt-1 text-gray-900 flex items-start">
                      <FiMapPin className="mr-2 text-gray-400 mt-0.5" size={14} />
                      {ticket.organisation?.adresse || 'Non spécifié'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Dates importantes */}
              <div className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm">
                <h3 className="text-lg font-semibold text-[#875A7B] mb-4 flex items-center">
                  <FiLayers className="mr-2" /> Dates importantes
                </h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-500">Deadline client:</label>
                    <p className="mt-1 text-gray-900">{formatDate(ticket.deadline_client)}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-3 border-t border-gray-200 flex justify-end sticky bottom-0">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-[#875A7B] text-white rounded-md hover:bg-[#6d4a63] transition-colors"
          >
            Fermer
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default DemandeDetail;